// ─── src/components/dashboard/MentorsTable.tsx ───────────────────────────────
import { useState, type FC } from "react";
import { Eye, ExternalLink, MapPin } from "lucide-react";
import type { Mentor } from "../../types/mentor";
import { avatarColor, initials } from "../../utils/formatter";
import { MentorDetailModal } from "./MentorDetailModal";

interface Props {
  mentors: Mentor[];
}

const COL_HEADERS = [
  "Mentor", "Company / Role", "Topics", "Cities", "Mode", "Frequency", "Actions",
];

const COL_WIDTHS: Record<string, string> = {
  "Frequency": "w-40",
};

const FREQ_BADGE: Record<string, string> = {
  "on-demand": "bg-brand-orange/20 text-brand-orange border border-brand-orange/30",
  monthly:     "bg-teal-500/20 text-teal-300 border border-teal-500/30",
  quarterly:   "bg-indigo-500/20 text-indigo-300 border border-indigo-500/30",
};

export const MentorsTable: FC<Props> = ({ mentors }) => {
  const [selected, setSelected] = useState<Mentor | null>(null);

  return (
    <>
      <div className="overflow-x-auto rounded-2xl border border-brand-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-brand-border bg-brand-border/30">
              {COL_HEADERS.map((h) => (
                <th
                  key={h}
                  className={`px-4 py-3 text-left text-xs font-semibold
                             text-brand-muted uppercase tracking-wider
                             ${COL_WIDTHS[h] ?? ""}`}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {mentors.map((mentor, i) => (
              <MentorRow
                key={mentor.id ?? i}
                mentor={mentor}
                onView={() => setSelected(mentor)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <MentorDetailModal
          mentor={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
};

// ── Row ────────────────────────────────────────────────────────────────────────
const MentorRow: FC<{ mentor: Mentor; onView: () => void }> = ({ mentor, onView }) => {
  const visibleTopics  = mentor.topics.slice(0, 2);
  const remainingCount = mentor.topics.length - visibleTopics.length;

  return (
    <tr className="border-b border-brand-border/50 hover:bg-brand-border/20 transition-colors">
      {/* Mentor */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div
            className={`${avatarColor(mentor.name)} w-9 h-9 rounded-full
                        flex items-center justify-center text-white text-xs font-bold shrink-0`}
          >
            {initials(mentor.name)}
          </div>
          <div className="min-w-0">
            <p className="font-medium text-white truncate max-w-[150px]">{mentor.name}</p>
            <p className="text-brand-muted text-xs truncate max-w-[150px]">{mentor.email}</p>
          </div>
        </div>
      </td>

      {/* Company / Role */}
      <td className="px-4 py-3">
        <p className="text-white text-sm truncate max-w-[180px]">{mentor.currentRole}</p>
        <p className="text-brand-muted text-xs truncate max-w-[180px]">{mentor.company}</p>
      </td>

      {/* Topics */}
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1">
          {visibleTopics.map((t) => (
            <span
              key={t}
              className="px-2 py-0.5 rounded-full text-xs font-medium
                         bg-brand-orange/15 text-brand-orange border border-brand-orange/25"
            >
              {t}
            </span>
          ))}
          {remainingCount > 0 && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium
                             bg-brand-border text-brand-muted border border-brand-border">
              +{remainingCount}
            </span>
          )}
        </div>
      </td>

      {/* Cities */}
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1">
          {mentor.cities.slice(0, 2).map((c) => (
            <span
              key={c}
              className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs
                         bg-teal-500/15 text-teal-300 border border-teal-500/25"
            >
              <MapPin size={9} />{c}
            </span>
          ))}
          {mentor.cities.length > 2 && (
            <span className="px-2 py-0.5 rounded-full text-xs text-brand-muted">
              +{mentor.cities.length - 2}
            </span>
          )}
        </div>
      </td>

      {/* Mode */}
      <td className="px-4 py-3">
        <div className="flex gap-1 flex-wrap">
          {mentor.mode.map((m) => (
            <span
              key={m}
              className={`px-2 py-0.5 rounded-full text-xs font-medium border
                ${m === "Online"
                  ? "bg-blue-500/20 text-blue-300 border-blue-500/30"
                  : "bg-emerald-500/20 text-emerald-300 border-emerald-500/30"
                }`}
            >
              {m}
            </span>
          ))}
        </div>
      </td>

      {/* Frequency */}
      <td className="px-4 py-3 w-40">
        <span
          className={`inline-block px-3 py-1 rounded-full text-xs font-medium capitalize whitespace-nowrap
                      ${FREQ_BADGE[mentor.frequency] ?? "bg-gray-500/20 text-gray-300 border border-gray-500/30"}`}
        >
          {mentor.frequency}
        </span>
      </td>

      {/* Actions */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <button
            onClick={onView}
            title="View details"
            className="p-1.5 rounded-lg text-brand-muted hover:text-white
                       hover:bg-brand-border transition-colors"
          >
            <Eye size={15} />
          </button>
          {mentor.linkedin && (
            <a
              href={mentor.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              title="LinkedIn"
              className="p-1.5 rounded-lg text-brand-muted hover:text-brand-orange
                         hover:bg-brand-border transition-colors"
            >
              <ExternalLink size={15} />
            </a>
          )}
        </div>
      </td>
    </tr>
  );
};
